import React, { useEffect, useState, useRef } from 'react';
import { HSLColor, hslToHex, hslToRgb } from '../utils/colorUtils';
import { Check, Copy, X } from 'lucide-react';
interface ColorPickerPopupProps {
  initialColor: HSLColor;
  onApply: (color: HSLColor) => void;
  onChange?: (color: HSLColor) => void;
  onClose: () => void;
}
export function ColorPickerPopup({
  initialColor,
  onApply,
  onChange,
  onClose
}: ColorPickerPopupProps) {
  const [color, setColor] = useState<HSLColor>(initialColor);
  const [copied, setCopied] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);
  // Notify parent of color changes in real-time
  useEffect(() => {
    onChange?.(color);
  }, [color, onChange]);
  const handleHueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setColor({
      ...color,
      h: parseInt(e.target.value)
    });
  };
  const handleSatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setColor({
      ...color,
      s: parseInt(e.target.value)
    });
  };
  const handleLightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setColor({
      ...color,
      l: parseInt(e.target.value)
    });
  };
  const copyHex = () => {
    navigator.clipboard.writeText(hslToHex(color.h, color.s, color.l));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const hex = hslToHex(color.h, color.s, color.l);
  const rgb = hslToRgb(color.h, color.s, color.l);
  // Gradient backgrounds for sliders
  const hueGradient = 'linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)';
  const satGradient = `linear-gradient(to right, hsl(${color.h}, 0%, ${color.l}%), hsl(${color.h}, 100%, ${color.l}%))`;
  const lightGradient = `linear-gradient(to right, hsl(${color.h}, ${color.s}%, 0%), hsl(${color.h}, ${color.s}%, 50%), hsl(${color.h}, ${color.s}%, 100%))`;
  return <div ref={popupRef} className="absolute top-full left-0 mt-2 z-[200] w-72 bg-card border border-[hsl(var(--border)/0.2)] rounded-xl shadow-2xl backdrop-blur-xl p-4 animate-in fade-in zoom-in-95 duration-200" data-id="element-71">
      {/* Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-72">
        <h3 className="text-sm font-semibold text-foreground" data-id="element-73">Edit Color</h3>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-id="element-74">
          <X className="w-4 h-4" data-id="element-75" />
        </button>
      </div>

      {/* Preview */}
      <div className="w-full h-24 rounded-lg shadow-inner mb-4 border border-[hsl(var(--border)/0.1)]" style={{
      backgroundColor: `hsl(${color.h}, ${color.s}%, ${color.l}%)`
    }} data-id="element-76" />

      {/* Sliders */}
      <div className="space-y-4 mb-6" data-id="element-77">
        {/* Hue */}
        <div className="space-y-1.5" data-id="element-78">
          <div className="flex justify-between text-xs text-muted-foreground" data-id="element-79">
            <span data-id="element-80">Hue</span>
            <span data-id="element-81">{color.h}°</span>
          </div>
          <input type="range" min="0" max="360" value={color.h} onChange={handleHueChange} className="w-full h-3 rounded-full appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50" style={{
          background: hueGradient
        }} data-id="element-82" />
        </div>

        {/* Saturation */}
        <div className="space-y-1.5" data-id="element-83">
          <div className="flex justify-between text-xs text-muted-foreground" data-id="element-84">
            <span data-id="element-85">Saturation</span>
            <span data-id="element-86">{color.s}%</span>
          </div>
          <input type="range" min="0" max="100" value={color.s} onChange={handleSatChange} className="w-full h-3 rounded-full appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50" style={{
          background: satGradient
        }} data-id="element-87" />
        </div>

        {/* Lightness */}
        <div className="space-y-1.5" data-id="element-88">
          <div className="flex justify-between text-xs text-muted-foreground" data-id="element-89">
            <span data-id="element-90">Lightness</span>
            <span data-id="element-91">{color.l}%</span>
          </div>
          <input type="range" min="0" max="100" value={color.l} onChange={handleLightChange} className="w-full h-3 rounded-full appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50" style={{
          background: lightGradient
        }} data-id="element-92" />
        </div>
      </div>

      {/* Values */}
      <div className="space-y-2 mb-6 bg-secondary/30 p-3 rounded-lg border border-[hsl(var(--border)/0.1)]" data-id="element-93">
        <div className="flex items-center justify-between group" data-id="element-94">
          <span className="text-xs text-muted-foreground font-mono" data-id="element-95">HEX</span>
          <button onClick={copyHex} className="flex items-center gap-2 text-xs font-mono hover:text-primary transition-colors" data-id="element-96">
            {hex}
            {copied ? <Check className="w-3 h-3 text-green-500" data-id="element-97" /> : <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-98" />}
          </button>
        </div>
        <div className="flex items-center justify-between" data-id="element-99">
          <span className="text-xs text-muted-foreground font-mono" data-id="element-100">RGB</span>
          <span className="text-xs font-mono" data-id="element-101">{rgb}</span>
        </div>
        <div className="flex items-center justify-between" data-id="element-102">
          <span className="text-xs text-muted-foreground font-mono" data-id="element-103">HSL</span>
          <span className="text-xs font-mono" data-id="element-104">
            {color.h}, {color.s}%, {color.l}%
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2" data-id="element-105">
        <button onClick={onClose} className="flex-1 px-3 py-2 text-sm font-medium bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors border border-[hsl(var(--border)/0.1)]" data-id="element-106">
          Cancel
        </button>
        <button onClick={() => onApply(color)} className="flex-1 px-3 py-2 text-sm font-medium bg-primary text-black rounded-md hover:bg-primary/90 transition-colors shadow-sm" data-id="element-107">
          Apply
        </button>
      </div>
    </div>;
}